<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['export']);

const { t } = useI18n();
const route = useRoute();

const dialogRef = ref(null);
const exportMethod = ref('direct');

const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const uiFlags = useMapGetter('contacts/getUIFlags');
const isExportingContact = computed(() => uiFlags.value.isExporting);

const activeSegmentId = computed(() => route.params.segmentId);
const activeSegment = computed(() =>
  activeSegmentId.value
    ? segments.value.find(view => view.id === Number(activeSegmentId.value))
    : undefined
);

const exportContacts = async () => {
  let query = { payload: [] };

  if (activeSegmentId.value && activeSegment.value) {
    query = activeSegment.value.query;
  } else if (Object.keys(appliedFilters.value).length > 0) {
    query = filterQueryGenerator(appliedFilters.value);
  }

  emit('export', {
    ...query,
    label: route.params.label || '',
    direct: exportMethod.value === 'direct',
  });
};

const handleDialogConfirm = async () => {
  await exportContacts();
  dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.TITLE')"
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.CONFIRM')
    "
    :is-loading="isExportingContact"
    :disable-confirm-button="isExportingContact"
    @confirm="handleDialogConfirm"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DESCRIPTION') }}
      </p>

      <div class="flex flex-col gap-3">
        <span class="text-xs font-semibold text-n-slate-12 uppercase tracking-wider">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.METHOD_LABEL') }}
        </span>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <!-- Direct Download Option -->
          <label
            class="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all duration-200 select-none"
            :class="[
              exportMethod === 'direct'
                ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
                : 'border-n-weak hover:border-n-slate-6 bg-n-alpha-1'
            ]"
          >
            <input
              type="radio"
              v-model="exportMethod"
              value="direct"
              class="mt-1 text-n-brand focus:ring-n-brand border-n-weak"
            />
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.DIRECT_DOWNLOAD') }}
              </span>
              <span class="text-xs text-n-slate-11">
                Download CSV directly in your browser with UTMs included
              </span>
            </div>
          </label>

          <!-- Email Delivery Option -->
          <label
            class="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all duration-200 select-none"
            :class="[
              exportMethod === 'email'
                ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
                : 'border-n-weak hover:border-n-slate-6 bg-n-alpha-1'
            ]"
          >
            <input
              type="radio"
              v-model="exportMethod"
              value="email"
              class="mt-1 text-n-brand focus:ring-n-brand border-n-weak"
            />
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.EMAIL_DELIVERY') }}
              </span>
              <span class="text-xs text-n-slate-11">
                Generate export in background and receive download link via email
              </span>
            </div>
          </label>
        </div>
      </div>
    </div>
  </Dialog>
</template>
